/**
 * `futurex verify [--list]` / `futurex test` — run the project's diagnostics
 * (typecheck/lint/test/build) and report structured pass/fail results. Runs the
 * checks WITHOUT blocking the event loop, so the REPL's chatbox (spinner,
 * Ctrl+C, queued input) stays alive during a slow typecheck. This is the
 * diagnostics surface the repair loop ({@link ./repair.ts}) feeds from.
 */
import { findRepoRoot } from './repo.js';
import { detectChecks, runDiagnosticsAsync } from './diagnostics.js';
import { color, error, info, line, startSpinner, verdictLine } from './ui.js';

export async function runVerify(
  opts: { list?: boolean; only?: string; signal?: AbortSignal } = {},
): Promise<void> {
  const root = findRepoRoot();
  let checks = detectChecks(root);
  if (opts.only) checks = checks.filter((c) => c.name === opts.only);
  if (checks.length === 0) {
    info(opts.only ? `no \`${opts.only}\` script detected` : 'no typecheck/lint/test/build scripts detected');
    return;
  }
  if (opts.list) {
    info(`detected checks (${checks.length}):`);
    for (const c of checks) line(`  ${c.name} → ${c.command}`);
    return;
  }

  info(`running ${checks.length} check${checks.length === 1 ? '' : 's'} in ${root} …`);
  const spin = startSpinner('verifying…');
  let results;
  try {
    results = await runDiagnosticsAsync(root, {
      checks,
      ...(opts.signal ? { signal: opts.signal } : {}),
    });
  } finally {
    spin.stop();
  }
  let failed = 0;
  for (const r of results) {
    const state = r.timedOut ? 'timed out' : '';
    line(verdictLine(r.ok, `${r.name} ${state}${color.dim(`(${(r.durationMs / 1000).toFixed(1)}s)`)}`));
    if (!r.ok) {
      failed++;
      if (r.failingFiles.length > 0) {
        line(color.dim(`      files: ${r.failingFiles.slice(0, 8).join(', ')}`));
      }
    }
  }
  if (failed > 0) {
    error(`${failed}/${results.length} checks failed`);
    process.exitCode = 1;
  } else {
    info(`all ${results.length} checks passed`);
  }
}
